import firebase from 'firebase/app';

/**
 * Built-in search (replaces Algolia). Downloads the search file the server builds at
 * `_search/meta` (see platform/functions/src/search.ts), then searches in the browser.
 * Reading it needs a role, so it follows the same permissions as the data itself.
 */

type RecordRow = [string, string, string, string]; // id, projectID, title, keyNumber
type NoteRow = [string, string, string, string]; // id, projectID, title, body
type VideoRow = [string, string, string]; // id (file name), recordID, notes
type AssetRow = [string, string]; // id (file name), projectID

type SearchFile = {
  v: number;
  builtAt: string;
  projects: IProject[];
  records: RecordRow[];
  notes: NoteRow[];
  videos: VideoRow[];
  assets: AssetRow[];
};

type Loaded = {
  version: number;
  projects: Map<string, IProject>;
  /** token -> set of project IDs whose project, records, notes, videos or assets contain it */
  tokens: Map<string, Set<string>>;
  /** project-level tokens only, used to rank direct project matches first */
  projectTokens: Map<string, Set<string>>;
  sortedTokens: string[];
  /** projectID -> number of its records that have no video/audio file (Admin › Needs attention) */
  emptyRecords: Map<string, number>;
};

const MAX_RESULTS = 100;
const META_CHECK_MS = 60000;

export const normalise = (text: string): string[] =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0);

const add = (map: Map<string, Set<string>>, text: unknown, projectID: string) => {
  if (!projectID || text == null) return;
  for (const token of normalise(String(text))) {
    let set = map.get(token);
    if (!set) map.set(token, (set = new Set()));
    set.add(projectID);
  }
};

export function buildIndex(file: SearchFile, version: number): Loaded {
  const projects = new Map(file.projects.map((p) => [p.id, p]));
  const tokens = new Map<string, Set<string>>();
  const projectTokens = new Map<string, Set<string>>();

  for (const p of file.projects) {
    const fields = [p.title, p.client, p.agency, p.jobNumber, ...(Array.isArray(p.tags) ? p.tags : [])];
    for (const cf of [p.customField1, p.customField2, p.customField3]) if (cf && cf.value) fields.push(cf.value);
    for (const f of fields) {
      add(tokens, f, p.id);
      add(projectTokens, f, p.id);
    }
  }
  const recordProject = new Map<string, string>();
  for (const [id, projectID, title, keyNumber] of file.records) {
    recordProject.set(id, projectID);
    add(tokens, title, projectID);
    add(tokens, keyNumber, projectID);
  }
  for (const [, projectID, title, body] of file.notes) {
    add(tokens, title, projectID);
    add(tokens, body, projectID);
  }
  for (const [id, recordID, notes] of file.videos) {
    const projectID = recordProject.get(recordID) ?? '';
    add(tokens, id, projectID);
    add(tokens, notes, projectID);
  }
  for (const [id, projectID] of file.assets) add(tokens, id, projectID);

  const recordsWithFiles = new Set(file.videos.map((v) => v[1]));
  const emptyRecords = new Map<string, number>();
  for (const [id, projectID] of file.records) {
    if (!recordsWithFiles.has(id)) emptyRecords.set(projectID, (emptyRecords.get(projectID) || 0) + 1);
  }

  return { version, projects, tokens, projectTokens, sortedTokens: Array.from(tokens.keys()).sort(), emptyRecords };
}

/** The "Needs attention" lists on Admin › Overview, as project lists the home page can show. */
export type AttentionKind = 'incomplete' | 'noClient' | 'emptyRecords';
export const ATTENTION_LABEL: Record<AttentionKind, string> = {
  incomplete: 'Incomplete projects',
  noClient: 'Projects with no client',
  emptyRecords: 'Projects with records that have no files',
};

export async function attentionProjects(kind: AttentionKind): Promise<IProject[]> {
  const idx = await ensureLoaded();
  const all = Array.from(idx.projects.values());
  if (kind === 'incomplete') return all.filter((p) => !p.complete);
  if (kind === 'noClient') return all.filter((p) => !String(p.client || '').trim());
  return all.filter((p) => idx.emptyRecords.has(p.id));
}

/** Edit distance, stopping early once it exceeds 1. */
const withinOneEdit = (a: string, b: string): boolean => {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > 1) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

/** How well an index word matches a query word. */
const EXACT = 3;
const PREFIX = 2;
const TYPO = 1;

/**
 * Index words matching one query word: exact, prefix, or (for words of 5+ letters with no
 * digits) one typo. Codes like job numbers get no typo tolerance, so ACC097 never finds ACC096.
 */
const matchingTokens = (index: Loaded, q: string): [string, number][] => {
  const out: [string, number][] = [];
  // Binary search to the first word >= q, then walk while words start with q.
  let lo = 0;
  let hi = index.sortedTokens.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (index.sortedTokens[mid] < q) lo = mid + 1;
    else hi = mid;
  }
  for (let k = lo; k < index.sortedTokens.length && index.sortedTokens[k].startsWith(q); k++) {
    out.push([index.sortedTokens[k], index.sortedTokens[k] === q ? EXACT : PREFIX]);
  }
  if (q.length >= 5 && /^[a-z]+$/.test(q)) {
    // Compare against the word's first q.length-1, q.length and q.length+1 letters, so a
    // typo still matches the start of a longer word ("refinry" finds "refinery").
    for (const t of index.sortedTokens) {
      if (t.startsWith(q) || t.length < q.length - 1) continue;
      for (let len = q.length - 1; len <= q.length + 1; len++) {
        if (len <= t.length && withinOneEdit(q, t.slice(0, len))) {
          out.push([t, TYPO]);
          break;
        }
      }
    }
  }
  return out;
};

/**
 * Projects matching every word of the query, directly or through their records, notes,
 * videos or assets. Ranked by match quality (exact > prefix > typo, with a bonus when the
 * word is on the project itself), then newest first.
 */
export function searchIndex(index: Loaded, query: string, limit: number = MAX_RESULTS): IProject[] {
  const words = normalise(query);
  if (!words.length) return [];

  let total: Map<string, number> | null = null;
  for (const word of words) {
    const best = new Map<string, number>();
    const consider = (id: string, score: number) => {
      if ((best.get(id) || 0) < score) best.set(id, score);
    };
    for (const [t, quality] of matchingTokens(index, word)) {
      index.tokens.get(t)?.forEach((id) => consider(id, quality * 2));
      index.projectTokens.get(t)?.forEach((id) => consider(id, quality * 2 + 1));
    }
    if (total === null) total = best;
    else {
      const previous: Map<string, number> = total;
      const next = new Map<string, number>();
      best.forEach((score, id) => {
        if (previous.has(id)) next.set(id, (previous.get(id) as number) + score);
      });
      total = next;
    }
    if (total.size === 0) return [];
  }

  const scores = total as Map<string, number>;
  return Array.from(scores.keys())
    .map((id) => index.projects.get(id))
    .filter((p): p is IProject => !!p)
    .sort((a, b) => (scores.get(b.id) as number) - (scores.get(a.id) as number) || (b.date || 0) - (a.date || 0))
    .slice(0, limit);
}
// ---- loading -------------------------------------------------------------

let loaded: Loaded | null = null;
let loading: Promise<Loaded> | null = null;
let lastMetaCheck = 0;

const gunzipToText = async (bytes: Uint8Array): Promise<string> => {
  const DS = (window as any).DecompressionStream;
  if (!DS) throw new Error('This browser is too old for built-in search (no DecompressionStream).');
  // `as any`: TypeScript 3.7's browser types predate Blob.stream().
  const stream = (new Blob([bytes]) as any).stream().pipeThrough(new DS('gzip'));
  return new Response(stream).text();
};

async function download(attempt = 1): Promise<Loaded> {
  const db = firebase.firestore();
  const meta = await db.doc('_search/meta').get();
  const m = meta.data();
  if (!m || !m.version) throw new Error('The search file has not been built yet.');

  const parts = await Promise.all(
    Array.from({ length: m.chunkCount }, (_, i) => db.doc(`_search/meta/chunks/${i}`).get()),
  );
  if (parts.some((p) => p.get('version') !== m.version)) {
    // A rebuild was in progress. Try again shortly.
    if (attempt >= 3) throw new Error('The search file is being rebuilt. Try again in a minute.');
    await new Promise((r) => setTimeout(r, 2000 * attempt));
    return download(attempt + 1);
  }
  const arrays = parts.map((p) => (p.get('data') as firebase.firestore.Blob).toUint8Array());
  const total = arrays.reduce((n, a) => n + a.length, 0);
  const joined = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    joined.set(a, offset);
    offset += a.length;
  }
  const file = JSON.parse(await gunzipToText(joined)) as SearchFile;
  return buildIndex(file, m.version);
}

async function ensureLoaded(): Promise<Loaded> {
  const now = Date.now();
  if (loaded && now - lastMetaCheck < META_CHECK_MS) return loaded;
  if (loading) return loading;
  loading = (async () => {
    try {
      if (loaded) {
        // Only download again if the server has built a newer file.
        lastMetaCheck = now;
        const meta = await firebase.firestore().doc('_search/meta').get();
        if (meta.get('version') === loaded.version) return loaded;
      }
      loaded = await download();
      lastMetaCheck = Date.now();
      return loaded;
    } finally {
      loading = null;
    }
  })();
  return loading;
}

/** Start downloading in the background, e.g. when the search box gets focus. */
export const preloadSearch = () => {
  ensureLoaded().catch(() => undefined);
};

/** Every project in the search file (for auto-suggest and the tag filter). */
export async function allProjects(): Promise<IProject[]> {
  return Array.from((await ensureLoaded()).projects.values());
}

/** The pop-up while typing: the best 100. */
export async function builtinSearch(query: string): Promise<IProject[]> {
  return searchIndex(await ensureLoaded(), query);
}

/** Enter in the search box: every matching project, best matches first (Aaron, 5 Nov 2026). */
export async function builtinSearchAll(query: string): Promise<IProject[]> {
  return searchIndex(await ensureLoaded(), query, Number.MAX_SAFE_INTEGER);
}
